<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { BaseButton, BaseIcon } from '@magma-app/magma-lapilli';

import { useRewardRetrievalTimeline } from './composables/useRewardRetrievalTimeline';

const props = withDefaults(
  defineProps<{
    status?: string;
    retrievalInstruction?: string;
    contactEmail?: string | null;
    showContactButton?: boolean;
    highlightCurrentWhenNotPurchased?: boolean;
  }>(),
  {
    status: undefined,
    retrievalInstruction: undefined,
    contactEmail: null,
    showContactButton: true,
    highlightCurrentWhenNotPurchased: false,
  }
);

const emit = defineEmits<{
  (event: 'contact-admin'): void;
}>();

const { t } = useI18n({ useScope: 'global' });

const steps = computed(() =>
  useRewardRetrievalTimeline({
    status: props.status,
    retrievalInstruction: props.retrievalInstruction,
    t,
  })
);

const isPurchased = computed(() => props.status === 'purchased');

const stepTextClass = (isFirst?: boolean, isCurrent?: boolean) => {
  if (!isPurchased.value) {
    if (props.highlightCurrentWhenNotPurchased && isCurrent) {
      return 'text-foreground-emphasis';
    }
    return 'text-foreground-default';
  }
  if (isFirst) {
    return 'text-foreground-default';
  }
  if (isCurrent) {
    return 'text-foreground-emphasis';
  }
  return 'text-foreground-subtle';
};

const detailsTextClass = (isFirst?: boolean, isCurrent?: boolean) => {
  if (!isPurchased.value) {
    return 'text-foreground-default';
  }
  if (isFirst || isCurrent) {
    return 'text-foreground-default';
  }
  return 'text-foreground-subtle';
};
</script>

<template>
  <div v-for="(step, index) in steps" :key="index" class="flex px-4 md:px-8">
    <div class="relative mr-4 flex flex-col items-center">
      <div
        :class="{
          'top-8': step.isFirst,
          'top-0': !step.isFirst,
          'h-6': step.isLast,
          'bottom-0': !step.isLast
        }"
        class="absolute w-px rounded-b-sm bg-border-subtle"
      ></div>

      <BaseIcon
        class="relative z-10 mt-0.5"
        :icon="step.status.icon"
        :color="step.status.color"
        :bg-color="step.status.bgColor"
      />
    </div>

    <div class="pb-10">
      <p
        :class="stepTextClass(step.isFirst, step.isCurrent)"
        class="mt-1 text-mgm-txt-sm"
      >
        {{ step.step }}
      </p>

      <p
        :class="detailsTextClass(step.isFirst, step.isCurrent)"
        class="mt-1 text-mgm-txt-sm"
        v-html="step.details"
      ></p>

      <BaseButton
        v-if="
          showContactButton && step.isCurrent && isPurchased && contactEmail
        "
        :text="$t('generic.adminContact')"
        class="mt-2"
        size="md"
        state="scdr"
        @click="emit('contact-admin')"
      />
    </div>
  </div>
</template>
